import { Button } from "@/components/ui/button";
import { Video, Clock, Users, Eye, Radio } from "lucide-react";
import dashboardPreview from "@/assets/dashboard-preview.png";


const features = [
  {
    icon: Video,
    title: "Cameras On",
    description: "See and be seen for visual accountability"
  },
  {
    icon: Clock,
    title: "Shared Timers",
    description: "Synchronized Pomodoro sessions"
  },
  {
    icon: Users,
    title: "Ritual Check-ins",
    description: "Light interaction without pressure"
  },
  {
    icon: Eye,
    title: "Virtual Library",
    description: "Constant presence provides solidarity"
  }
];

export const StudyStream = () => {
  return (
    <section className="py-24 relative overflow-hidden" aria-labelledby="studystream-title">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/30 to-background" aria-hidden="true" />
      <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px]" aria-hidden="true" />
      
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Radio className="w-4 h-4 text-primary animate-pulse" aria-hidden="true" />
              <span className="text-sm font-semibold text-primary">LIVE STREAMS</span>
            </div>
            
            <h1 id="studystream-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              <span className="text-gradient">Study Stream</span>
            </h1>
          </div>

          {/* App Preview */}
          <div className="mb-16 animate-fade-in rounded-2xl overflow-hidden border border-border/50 shadow-2xl max-w-4xl mx-auto">
            <img src={dashboardPreview} alt="CSW dashboard showing study stats, tasks, and quick actions" className="w-full h-auto" loading="lazy" />
          </div>


          {/* Content Grid */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Description */}
            <article className="space-y-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/30 transition-all duration-300">
                <p className="text-lg text-foreground leading-relaxed mb-6">
                  CSW's Study Stream offers endless, free study streams you can join or host anytime. Participants keep cameras on, share a timer and occasionally check in, but there's no pressure to interact.
                </p>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Scholars who watch "Study With Me" or livestream study sessions describe how ritualised check‑ins and Pomodoro timers turn self‑discipline into visible accountability while creating a sense of companionship.
                </p>
                
                <p className="text-muted-foreground leading-relaxed">
                  Universities have even used platforms like Twitch to host live study streams, finding that they help off‑campus students feel connected and engaged.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Join a Stream
                  </a>
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Host Your Own
                  </a>
                </Button>
              </div>
            </article>

            {/* Right: Features Grid */}
            <aside className="space-y-4 animate-fade-in" style={{ animationDelay: '0.2s' }} aria-label="Study Stream features">
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div
                    key={feature.title}
                    className="group p-6 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/40 hover:bg-card/80 transition-all duration-300 animate-slide-in"
                    style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <feature.icon className="w-6 h-6 text-primary" aria-hidden="true" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                ))}
              </div>
              
              {/* Stats Banner */}
              <div className="p-6 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <p className="text-3xl font-bold text-gradient mb-1">∞</p>
                    <p className="text-xs text-muted-foreground">Free Streams</p>
                  </div>
                  <div className="w-px h-12 bg-border" aria-hidden="true" />
                  <div className="text-center flex-1">
                    <p className="text-3xl font-bold text-gradient mb-1">24/7</p>
                    <p className="text-xs text-muted-foreground">Availability</p>
                  </div>
                  <div className="w-px h-12 bg-border" aria-hidden="true" />
                  <div className="text-center flex-1">
                    <p className="text-3xl font-bold text-gradient mb-1">$0</p>
                    <p className="text-xs text-muted-foreground">Always Free</p>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
};